import { log } from "console";

export const ADDRESS_IP = 'backend-pscreen.praedium-tech.com';
export const ADDRESS_IP_URL = `https://${ADDRESS_IP}/`;
export const API_URL = `${ADDRESS_IP_URL}api`;
export const API_MEDIA_URL = `${ADDRESS_IP_URL}`;

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  status: number;
  statusText: string;

  constructor(message: string, status: number, statusText: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
  }
}

/**
 * FetchApi - Classe utilitaire pour les appels API
 *
 * Utilise Token Authentication uniquement (pas de refresh token)
 * Ajoute automatiquement:
 * - Le token d'authentification depuis localStorage
 * - L'organization_id active pour les requêtes POST
 *
 * Gestion des erreurs:
 * - 401: Token invalide ou expiré -> redirection automatique vers login
 * - Autres erreurs: Exception levée avec détails
 */
export class FetchApi {
  /**
   * Gérer les erreurs de réponse HTTP
   * Déconnecte automatiquement l'utilisateur si 401 (Unauthorized)
   */
  private static async handleResponse(res: Response): Promise<any> {
    if (!res.ok) {
      // Si 401 Unauthorized, déconnecter l'utilisateur
      if (res.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('active_center_id');
        // Rediriger vers login si on n'y est pas déjà
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        throw new ApiError('Session expirée. Veuillez vous reconnecter.', 401, res.statusText);
      }

      const errorText = await res.text();
      throw new ApiError(errorText || 'Erreur lors de la requête', res.status, res.statusText);
    }

    return res.json();
  }

  /**
   * POST - Envoyer des données JSON
   * @param url URL de l'endpoint
   * @param payload Données à envoyer
   * @param token Token optionnel (utilise localStorage par défaut)
   */
  static async post<T>(url: string, payload?: any, token?: string): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const local_token = localStorage.getItem('token');
    if (local_token) {
      headers["Authorization"] = `Token ${local_token}`;
    }
    const res = await fetch(url, {
      method: "POST",
      headers,
      body: payload ? JSON.stringify({...payload, organization: localStorage.getItem('active_center_id')}) : undefined,
    });
    return this.handleResponse(res);
  }
 

  /**
   * GET - Récupérer des données
   * @param url URL de l'endpoint
   * @param token Token optionnel (utilise localStorage par défaut)
   */
  static async get<T>(url: string, token?: string): Promise<T> {
    const headers: Record<string, string> = {};
    const local_token = localStorage.getItem('token');
    if (local_token) {
      headers["Authorization"] = `Token ${local_token}`;
    }
    const res = await fetch(url, {
      method: "GET",
      headers,
    });
    return this.handleResponse(res);
  }

  /**
   * PUT - Mettre à jour des données JSON
   * @param url URL de l'endpoint
   * @param payload Données à mettre à jour
   * @param token Token optionnel (utilise localStorage par défaut)
   */
  static async put<T>(url: string, payload?: any, token?: string): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const local_token = localStorage.getItem('token');
    if (local_token) {
      headers["Authorization"] = `Token ${local_token}`;
    }
    const res = await fetch(url, {
      method: "PUT",
      headers,
      body: payload ? JSON.stringify(payload) : undefined,
    });
    return this.handleResponse(res);
  }

  /**
   * DELETE - Supprimer des données
   * @param url URL de l'endpoint
   * @param token Token optionnel (utilise localStorage par défaut)
   */
  static async delete<T>(url: string, token?: string): Promise<T> {
    const headers: Record<string, string> = {};
    const local_token = localStorage.getItem('token');
    if (local_token) {
      headers["Authorization"] = `Token ${local_token}`;
    }
    const res = await fetch(url, {
      method: "DELETE",
      headers,
    });
    return this.handleResponse(res);
  }












  /**
   * POST FILE - Envoyer des fichiers (FormData)
   * @param url URL de l'endpoint
   * @param payload FormData ou objet à convertir en FormData
   * @param token Token optionnel (utilise localStorage par défaut)
   */
  static async postFile<T>(url: string, payload?: any, token?: string): Promise<T> {
    const headers: Record<string, string> = {};
    const local_token = localStorage.getItem('token');
    if (local_token) {
      headers["Authorization"] = `Token ${local_token}`;
    }

    let body: BodyInit | undefined;

    if (payload instanceof FormData) {
      // Si c'est déjà un FormData, on y ajoute l'organisation
      payload.append('organization', localStorage.getItem('active_center_id') || '');
      body = payload;
      // ⚠️ Ne pas définir Content-Type ici, fetch le gère automatiquement
    } else if (payload) {
      // Sinon, on crée un FormData à partir de l'objet
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        // Gérer les tableaux en les convertissant en chaînes séparées par des virgules
        if (Array.isArray(value)) {
          formData.append(key, value.join(','));
        } else if (value !== null && value !== undefined) {
          formData.append(key, value as any);
        }
      });
      formData.append('organization', localStorage.getItem('active_center_id') || '');
      body = formData;
    }

    const res = await fetch(url, {
      method: "POST",
      headers,
      body,
    });

    return this.handleResponse(res);
  }

  /**
   * PUT FILE - Mettre à jour avec des fichiers (FormData)
   * @param url URL de l'endpoint
   * @param payload FormData ou objet à convertir en FormData
   * @param token Token optionnel (utilise localStorage par défaut)
   */
  static async putFile<T>(url: string, payload?: any, token?: string): Promise<T> {
    const headers: Record<string, string> = {};
    const local_token = localStorage.getItem('token');
    if (local_token) {
      headers["Authorization"] = `Token ${local_token}`;
    }

    let body: BodyInit | undefined;

    if (payload instanceof FormData) {
      body = payload;
    } else if (payload) {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        // Gérer les tableaux en les convertissant en chaînes séparées par des virgules
        if (Array.isArray(value)) {
          formData.append(key, value.join(','));
        } else if (value !== null && value !== undefined) {
          formData.append(key, value as any);
        }
      });
      body = formData;
    }

    const res = await fetch(url, {
      method: "PUT",
      headers,
      body,
    });

    return this.handleResponse(res);
  }
}